/**
 * Drawing the shading scene.
 *
 * Lifted out of the React component for one reason: this is where the picture
 * either tells the truth or does not, and a function taking a canvas context can
 * be run in Node against a recording stub - which is how the ground shadows in
 * sceneDraw.test.js are checked to actually exist, land on the ground, and move
 * with the sun. Inside a useEffect none of that is reachable without a browser.
 *
 * Everything it needs is passed in. It holds no state and reads no DOM beyond
 * the context it is handed.
 */
import {
  DEG, v3, add, sub, mul, dot, cross, norm, compass, sunVector, shadowPoint,
  sunAboveGround,
} from "./vec3";
import { verticalSegments } from "./rowShading";
import { groundShadowPolygon } from "./groundShadow";
import { horizonElevationAt, beamBlockedByHorizon } from "./horizonProfile";
import { projectScenePoint } from "./sceneInteraction";
import { meshEdgeMasks } from "./meshEdges";
import { panelCellLines } from "./panelCellLines";
import { drawClearanceGuides } from "./clearanceDraw";
import { frontOccluder, screenBounds, boundsOverlap } from "./sceneOcclusion";

export const PALETTES = {
  dark: {
    sky: "#0d1a2b", ground: "#1b2a3a", grid: "rgba(255,255,255,0.10)",
    // Ground shadows are the point of the picture, so they are drawn at a
    // weight you cannot miss: at a low winter sun the shadow of one row reaches
    // most of the way to the next, and that is the thing a pitch decision turns
    // on. The earlier value was a polite tint that vanished against the grid.
    shadow: "rgba(3,8,15,0.72)", shadowEdge: "rgba(0,0,0,0.85)",
    rowShade: "rgba(4,10,18,0.62)",
    panel: "#6895bc", panelDim: "#23384f",
    edge: "rgba(255,255,255,0.35)", text: "#cfe0f5",
    bypass: "#f2a43c", sun: "#ffd166", arcFaint: "rgba(255,209,102,0.28)",
    mesh: [124, 138, 156], meshEdge: "rgba(255,255,255,0.10)",
    // Every roof plane, the one carrying the modules and the neighbouring
    // faces without any, in one colour; walls in the mesh grey.
    roof: [166, 133, 106],
    terrain: "rgba(90,110,130,0.55)", terrainEdge: "rgba(150,170,190,0.8)",
    sunGlow: "rgba(255,209,102,0.30)", sunCore: "#fff2c4",
  },
  light: {
    sky: "#eaf2fb", ground: "#e7edf3", grid: "rgba(51,65,85,0.14)",
    shadow: "rgba(28,45,70,0.50)", shadowEdge: "rgba(28,45,70,0.70)",
    rowShade: "rgba(24,46,74,0.34)",
    panel: "#5686b1", panelDim: "#20364e",
    edge: "rgba(20,40,70,0.45)", text: "#2b3d55",
    bypass: "#c9761c", sun: "#e8a33d", arcFaint: "rgba(184,124,40,0.35)",
    mesh: [186, 196, 208], meshEdge: "rgba(20,40,70,0.18)",
    roof: [209, 170, 137],
    terrain: "rgba(120,140,160,0.42)", terrainEdge: "rgba(80,100,125,0.75)",
    sunGlow: "rgba(232,163,61,0.28)", sunCore: "#fff6d8",
  },
};

/**
 * What the ground looks like, by the surface type chosen for the albedo
 * (pages/General.jsx SURFACE_OPTIONS). Opaque and muted: the shadows are
 * translucent dark washes and have to stay readable on every one of these.
 * A surface the list does not know, or none chosen, falls back to the
 * palette's neutral ground.
 */
export const GROUND_TONES = {
  light: {
    grass: "#cfe0c3", "fresh-grass": "#c4dfb6", soil: "#dccbb6", sand: "#e8dcc0",
    gravel: "#d8d8d2", concrete: "#dcdcd6", urban: "#d9d8d4", asphalt: "#c9ccd0",
    snow: "#f1f4f7", "fresh-snow": "#f6f8fa", sea: "#c6dbe6",
    aluminum: "#d9dde1", copper: "#dac9bf", "fresh-steel": "#d3d8dd", "dirty-steel": "#c8ccd0",
  },
  dark: {
    grass: "#1f3324", "fresh-grass": "#1f3a24", soil: "#352c24", sand: "#3b3628",
    gravel: "#2b2e30", concrete: "#2d2f30", urban: "#2c2e30", asphalt: "#23272b",
    snow: "#3a4149", "fresh-snow": "#404853", sea: "#1b2e3a",
    aluminum: "#2f3539", copper: "#3a2e29", "fresh-steel": "#2d3337", "dirty-steel": "#262a2d",
  },
};

export function groundTone(surfaceType, isDark) {
  const tones = isDark ? GROUND_TONES.dark : GROUND_TONES.light;
  return tones[String(surfaceType || "").toLowerCase()] || (isDark ? PALETTES.dark : PALETTES.light).ground;
}

/** Ground height under a point, for label placement. */
function groundZ0(geo, p) {
  return geo.groundZ(p.x, p.y);
}

/**
 * Draw one frame.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} o  W, H, isDark, scene, sun, state, geo, camera, rowCount,
 *                    showSegments, sunArcs, displayMesh
 */
export function drawShadingScene(ctx, o) {
  const { W, H, isDark, scene, sun, state, geo, camera, rowCount } = o;
  const { showSegments, sunArcs = [], displayMesh = null, horizon = null } = o;
  const cellLines = panelCellLines(o.moduleTopology, scene?.module?.portrait);
  const { supportMesh = null, roofBoundary = null } = o;
  const shadowMesh = o.shadowMesh || displayMesh;
  const groundShadowMesh = o.groundShadowMesh || displayMesh;
  const supportEdges = meshEdgeMasks(supportMesh);
  const displayEdges = o.solidMesh ? meshEdgeMasks(displayMesh) : null;
  // Only our generated closed solids have guaranteed outward winding.
  // Imported meshes remain double-sided; their winding may be inconsistent.
  const solidFaces = new Set();
  if (o.solidMesh) for (let t = 0; t < o.solidMesh.length; t += 9) {
    solidFaces.add(Array.from(o.solidMesh.subarray(t, t + 9)).join(","));
  }
  const printedArray = o.frame === "array" || o.frame === "edit";
  // The plant's other orientations, as fitted, drawn for context only.
  const contextRows = Array.isArray(o.contextRows) ? o.contextRows : [];
  if (!ctx || !scene || !sun || !state || !geo) return;

  ctx.clearRect(0, 0, W, H);
    const palette = isDark ? PALETTES.dark : PALETTES.light;

    ctx.fillStyle = palette.sky;
    ctx.fillRect(0, 0, W, H);

    const s = sunVector(sun.zenith, sun.azimuth);

    // Orthographic camera. The scene is a few tens of metres across and the
    // question is geometric, so a perspective frustum would add foreshortening
    // that makes two rows look like different sizes for no gain.
    const camDir = norm(
      v3(
        Math.sin(camera.az * DEG) * Math.cos(camera.el * DEG),
        Math.cos(camera.az * DEG) * Math.cos(camera.el * DEG),
        Math.sin(camera.el * DEG)
      )
    );
    // The screen's horizontal axis, written out rather than crossed with the
    // vertical. cross(z, camDir) normalises to exactly this for every elevation
    // between straight down and straight up - the elevation cancels - but it
    // becomes the zero vector AT straight down, and the whole frame then comes
    // out NaN. Looking straight down at the field is the most useful view there
    // is for checking a layout, so the camera has to survive arriving there.
    const right = v3(
      -Math.cos(camera.az * DEG), Math.sin(camera.az * DEG), 0
    );
    const up = cross(camDir, right);
    // Fit the scene to the frame instead of guessing a scale.
    //
    // The old version divided a fixed fraction of the canvas by a rough extent,
    // which was wrong in both directions at once: rows fell off the bottom while
    // the compass sat outside the picture. Here everything that must be visible
    // is projected once at unit scale, and the frame is fitted to that bounding
    // box - so it stays correct at any canvas size, row count, pitch and camera
    // angle, none of which this has to know about.
    // How much bare ground to draw around the array. Fixed, and a property of
    // the layout rather than of the hour: an apron sized from the current sun
    // grew as the sun dropped, so the whole scene zoomed out while the time
    // slider moved. Nothing was wrong with any single frame and it still read
    // as a fault, because the one thing a viewer holds constant while dragging
    // a slider is the frame. Shadows longer than this are handled where they
    // are drawn, not by resizing the world.
    const groundMargin = scene.pitch * (printedArray ? 0.5 : 2.2);
    let halfAcrossFit = (rowCount * scene.pitch) / 2 + groundMargin;
    let halfAlongFit = geo.rowLength / 2 + groundMargin;
    // The imported model has to be inside the picture too. It used not to be in
    // the fit at all, which was survivable while it was a shed beside the array
    // and is not survivable in whole-plant mode, where the model IS the array
    // and the row quads it replaces are no longer drawn.
    let meshTop = 0;
    if (displayMesh && displayMesh.length >= 9) {
      let across = 0;
      let along = 0;
      for (let t = 0; t < displayMesh.length; t += 3) {
        const a = Math.abs(displayMesh[t] * geo.c.x + displayMesh[t + 1] * geo.c.y);
        const b = Math.abs(displayMesh[t] * geo.u.x + displayMesh[t + 1] * geo.u.y);
        if (a > across) across = a;
        if (b > along) along = b;
        if (displayMesh[t + 2] > meshTop) meshTop = displayMesh[t + 2];
      }
      halfAcrossFit = Math.max(halfAcrossFit, across + groundMargin);
      halfAlongFit = Math.max(halfAlongFit, along + groundMargin);
    }
    const compassRadiusFit = Math.max(halfAcrossFit, halfAlongFit) * 1.02;
    const fitPoints = [];
    if ((!printedArray || o.frame === "edit") && supportMesh) {
      for (let i = 0; i < supportMesh.length; i += 3) {
        fitPoints.push(v3(supportMesh[i], supportMesh[i + 1], supportMesh[i + 2]));
      }
    }
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([a, b]) => {
      const p = add(mul(geo.c, a * halfAcrossFit), mul(geo.u, b * halfAlongFit));
      fitPoints.push(v3(p.x, p.y, geo.groundZ(p.x, p.y)));
    });
    if (!printedArray) {
      [0, 90, 180, 270].forEach((bearing) => {
        const d = compass(bearing);
        fitPoints.push(v3(d.x * compassRadiusFit, d.y * compassRadiusFit, 0));
      });
    }
    geo.rows.forEach((row) => {
      fitPoints.push(row.low[0], row.low[1], row.high[0], row.high[1]);
    });
    if (o.frame === "scene" && Array.isArray(geo.boundary)) {
      geo.boundary.forEach((p) => {
        fitPoints.push(v3(p.x, p.y, geo.groundZ(p.x, p.y)));
      });
    }
    // The neighbours are inside the frame too: a scene that fitted only its
    // own field would crop the plane beside it.
    contextRows.forEach((row) => {
      fitPoints.push(row.low[0], row.low[1], row.high[0], row.high[1]);
    });
    if (meshTop > 0) {
      // Its height, so a tall building is not cropped at the roofline.
      [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([a, b]) => {
        const p = add(mul(geo.c, a * halfAcrossFit), mul(geo.u, b * halfAlongFit));
        fitPoints.push(v3(p.x, p.y, meshTop));
      });
    }

    // The sky is a dome AROUND the scene, sized in METRES rather than pixels.
    // Deriving it from the canvas height gave a radius of a few metres inside a
    // hundred-metre array, so the sun path and the skyline were drawn among the
    // rows instead of above and beyond them - the horizon came out as an
    // ellipse lying across the modules.
    // A shared roof has one sky, centred on all faces, not on the selected array.
    const physicalPoints = [...geo.rows, ...contextRows].flatMap((r) => [...r.low, ...r.high]);
    for (const mesh of [supportMesh, displayMesh]) {
      for (let i = 0; i < (mesh?.length || 0); i += 3) physicalPoints.push(v3(mesh[i], mesh[i + 1], mesh[i + 2]));
    }
    let skyCenter = v3(0, 0, 0);
    let skyRadius = Math.max(halfAcrossFit, halfAlongFit, compassRadiusFit) * 1.25;
    // The parcel's size, roughly (its equivalent radius): the horizon and the
    // sun path are far away, and on a small site they are at least beyond
    // the plot's edge rather than wrapped round the building.
    const siteRadius = Number(o.siteRadius) > 0 ? Number(o.siteRadius) : 0;
    if ((scene.roof || contextRows.length) && physicalPoints.length) {
      const xs = physicalPoints.map((p) => p.x), ys = physicalPoints.map((p) => p.y), zs = physicalPoints.map((p) => p.z);
      skyCenter = v3((Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2,
        (Math.min(...zs) + Math.max(...zs)) / 2);
      const reach = Math.max(2, ...physicalPoints.map((p) => Math.hypot(p.x - skyCenter.x, p.y - skyCenter.y, p.z - skyCenter.z)));
      // Not hugging the house: a clear margin of eight metres, or half the
      // building's own size, between its walls and the dome the sun runs on.
      skyRadius = Math.max(reach * 1.25 + Math.max(8, reach * 0.5), siteRadius * 1.3);
      // The sky stands on the ground, as everything else in the scene does:
      // the far horizon (PVGIS measures it from the ground at the site) and
      // the sun path both rise from the ground ring, not from half way up the
      // building, where the skyline floated above the plot at eave height.
      skyCenter = v3(skyCenter.x, skyCenter.y, Math.min(...zs));
    } else {
      skyRadius = Math.max(skyRadius, siteRadius * 1.3);
    }
    const skyPoint = (dir, fraction = 1) => add(skyCenter, mul(dir, skyRadius * fraction));
    if (!printedArray || o.showSun === true) {
      for (let bearing = 0; bearing < 360; bearing += 30) {
        fitPoints.push(skyPoint(sunVector(90, bearing)));
      }
      // Whatever the arcs actually reach, so a June apex is inside the frame and
      // a December one does not leave half of it empty.
      sunArcs.forEach((arc) => {
        (arc.points || []).forEach((dir) => {
          if (dir) fitPoints.push(skyPoint(dir));
        });
      });
    }
    if (horizon && o.showHorizon !== false && !printedArray) {
      for (let bearing = 0; bearing < 360; bearing += 30) {
        const el = Math.max(0, horizonElevationAt(horizon, bearing));
        fitPoints.push(skyPoint(sunVector(90 - el, bearing)));
      }
    }

    // Geometry stays in the active roof's local frame; the camera and ground
    // stay in the shared project frame, including after a drag is released.
    const frame = scene.siteGeometry?.frame || {};
    const origin = v3(Number(frame.eastM) || 0, Number(frame.northM) || 0, Number(frame.elevationM) || 0);
    const workspace = o.workspace;
    if (workspace) {
      const shapeKey = JSON.stringify([supportMesh?.length, displayMesh?.length, geo.rows.length,
        scene.siteGeometry?.roof?.widthM, scene.siteGeometry?.roof?.depthM, contextRows.length]);
      if (workspace.shapeKey !== shapeKey) {
        workspace.shapeKey = shapeKey;
        workspace.fit = fitPoints.map((p) => add(p, origin));
        workspace.grid = null;
      }
      fitPoints.splice(0, fitPoints.length, ...workspace.fit.map((p) => sub(p, origin)));
    }
    const flat = fitPoints.map((p) => ({ x: dot(p, right), y: -dot(p, up) }));
    const minX = Math.min(...flat.map((p) => p.x));
    const maxX = Math.max(...flat.map((p) => p.x));
    const minY = Math.min(...flat.map((p) => p.y));
    const maxY = Math.max(...flat.map((p) => p.y));

    // Everything that must be visible is already in the box - ground, rows,
    // compass, dome, arcs - so the box is simply centred and fitted. The old
    // version anchored its CENTRE at two thirds height while allowing it 80% of
    // the height, which put the near rows off the bottom edge and left the top
    // third empty.
    const fitW = Math.max(1e-6, maxX - minX);
    const fitH = Math.max(1e-6, maxY - minY);
    const baseScale = camera.viewAnchor?.baseScale ?? Math.min((W * 0.98) / fitW, (H * 0.94) / fitH);
    const scale = baseScale * camera.zoom;
    // Preserve the same world pivot and physical scale when changing 2D/3D.
    const depth = fitPoints.reduce((sum, p) => sum + dot(p, camDir), 0) / fitPoints.length;
    const target = camera.viewAnchor?.target ?? add(origin, add(
      add(mul(right, (minX + maxX) / 2), mul(up, -(minY + maxY) / 2)), mul(camDir, depth)));
    const localTarget = sub(target, origin);
    const cx = dot(localTarget, right) * scale;
    const cy = -dot(localTarget, up) * scale;
    // The anchor the fitted box is hung from. Centred by default; the wheel
    // moves it so that zooming keeps whatever is under the pointer under the
    // pointer, instead of always pulling towards the middle of the frame.
    // Keep this transform fixed throughout a drag: re-fitting a moving object
    // would make the object slip away from the pointer.
    const locked = o.projection;
    const shift = locked ? sub(origin, locked.frameOrigin || origin) : v3(0, 0, 0);
    const view = locked ? { ...locked,
      originX: locked.originX + dot(shift, locked.right) * locked.scale,
      originY: locked.originY - dot(shift, locked.up) * locked.scale, frameOrigin: origin,
    } : { right, up, direction: camDir, scale, frameOrigin: origin, viewAnchor: { baseScale, target },
      originX: W / 2 + (camera.panX || 0) - cx,
      originY: H / 2 + (camera.panY || 0) - cy };
    const project = (p) => projectScenePoint(view, p);
    const projectedArea = (pts) => {
      let twice = 0;
      pts.forEach((p, i) => {
        const a = project(p);
        const b = project(pts[(i + 1) % pts.length]);
        twice += a.x * b.y - b.x * a.y;
      });
      return twice / 2;
    };
    const sameWinding = (pts) => projectedArea(pts) < 0 ? [...pts].reverse() : pts;
    const poly = (pts, fill, stroke, width = 1) => {
      if (!pts.length || pts.some((p) => !p)) return;
      ctx.beginPath();
      pts.forEach((p, i) => {
        const q = project(p);
        if (i === 0) ctx.moveTo(q.x, q.y);
        else ctx.lineTo(q.x, q.y);
      });
      ctx.closePath();
      if (fill) {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (stroke) {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = width;
        ctx.stroke();
      }
    };

    const outlineFacet = (points, mask, color) => {
      points.forEach((p, i) => {
        if (mask?.[i]) poly([p, points[(i + 1) % 3]], null, color, 0.8);
      });
    };
    // The ground: a square of the site's surface around the scene, as far
    // out as the horizon ring and growing with it, with a metre grid on it.
    // Its tone is the surface type chosen for the albedo, so a grass site is
    // green and an asphalt one grey rather than the same neutral wash. The
    // sky stays beyond it; edge-on (elevation zero) the plane is a line and
    // nothing is drawn.
    const groundFill = groundTone(o.surfaceType, isDark);
    const groundVisible = camDir.z > 0.02;
    // The ground plane under the scene, in the local frame. A building's base
    // is the ground on a roof site; elsewhere the terrain plane is.
    let groundLevel = workspace?.grid?.groundLevel;
    if (groundLevel == null && scene.roof && physicalPoints.length) {
      groundLevel = Math.min(...physicalPoints.map((p) => p.z)) + origin.z - 0.02;
    }
    if (workspace) workspace.grid = { groundLevel: groundLevel ?? null };
    const planeZ = groundLevel != null
      ? () => groundLevel - origin.z
      : (x, y) => geo.groundZ(x, y);
    // ... exactly on the ground plane drawn below, whatever frame it came from.
    if (groundLevel != null && (scene.roof || contextRows.length) && physicalPoints.length) {
      skyCenter = v3(skyCenter.x, skyCenter.y, groundLevel - origin.z);
    }
    // Shared-frame metres to a local point on the ground.
    const groundPoint = (x, y) => v3(x - origin.x, y - origin.y, planeZ(x - origin.x, y - origin.y));
    // The square, in shared-frame metres: centred where the dome is, a little
    // past the horizon ring so the ring stands on it.
    const groundHalf = Math.max(skyRadius * 1.15,
      ...physicalPoints.map((p) => 1.1 * Math.max(Math.abs(p.x - skyCenter.x), Math.abs(p.y - skyCenter.y))));
    const groundBox = { minX: skyCenter.x + origin.x - groundHalf, maxX: skyCenter.x + origin.x + groundHalf,
      minY: skyCenter.y + origin.y - groundHalf, maxY: skyCenter.y + origin.y + groundHalf };
    const groundSquare = [groundPoint(groundBox.minX, groundBox.minY), groundPoint(groundBox.maxX, groundBox.minY),
      groundPoint(groundBox.maxX, groundBox.maxY), groundPoint(groundBox.minX, groundBox.maxY)];
    const drawGround = () => {
      if (!groundVisible) return;
      poly(groundSquare, groundFill, null);
      // A metre grid over the square, on shared east/north coordinates so it
      // never follows the selected roof. Its step comes from the zoom - a line
      // every 36 px or more - so it reads as a ruler at any scale and the line
      // count stays bounded however far out the view is.
      const step = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000]
        .find((n) => n * view.scale >= 36) || 10000;
      const { minX, maxX, minY, maxY } = groundBox;
      const line = (p, q, color, width = 1) => {
        const a = project(p), b = project(q);
        ctx.strokeStyle = color; ctx.lineWidth = width;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      };
      for (let x = Math.ceil(minX / step) * step; x <= maxX; x += step)
        line(groundPoint(x, minY), groundPoint(x, maxY), palette.grid);
      for (let y = Math.ceil(minY / step) * step; y <= maxY; y += step)
        line(groundPoint(minX, y), groundPoint(maxX, y), palette.grid);
      // A hint of the surface: short strokes, leaning like grass blades on a
      // grass site. Decoration, not geometry.
      const textureStep = step * 2;
      const soft = isDark ? "rgba(220,235,225,0.055)" : "rgba(60,80,65,0.075)";
      const grass = String(o.surfaceType || "").includes("grass");
      for (let x = Math.ceil(minX / textureStep) * textureStep; x < maxX; x += textureStep) {
        for (let y = Math.ceil(minY / textureStep) * textureStep; y < maxY; y += textureStep) {
          const jitter = Math.sin(x * 12.9898 + y * 78.233) * 0.25;
          const a = groundPoint(x + textureStep * (0.45 + jitter), y + textureStep * 0.4);
          const b = add(a, v3(textureStep * (grass ? 0.04 : 0.1), textureStep * (grass ? 0.1 : 0.025), 0));
          line(a, b, soft);
          if (grass) line(a, add(a, v3(-textureStep * 0.04, textureStep * 0.07, 0)), soft);
        }
      }
    };

    // The ground under the array: the patch the shadows are clipped to. Same
    // tone as the ground around it, so it is not a lighter rectangle on the
    // site, and the grid runs over it. A roof without valid dimensions still
    // has a mathematical support plane for row placement and shading, but it
    // is not an actual roof surface, and that unbounded tilted plane is not
    // painted as if the entire site sloped. On a roof the supporting prism
    // already supplies the surface: a second opaque polygon at roof height
    // hides its walls and makes it look buried.
    const halfAcross = halfAcrossFit;
    const halfAlong = halfAlongFit;
    const groundAt = (a, b) => {
      const p = add(mul(geo.c, a), mul(geo.u, b));
      return v3(p.x, p.y, geo.groundZ(p.x, p.y));
    };
    const groundCorners = roofBoundary
      ? roofBoundary.map((p) => v3(p.x, p.y, geo.groundZ(p.x, p.y)))
      : [groundAt(-halfAcross, -halfAlong), groundAt(halfAcross, -halfAlong),
         groundAt(halfAcross, halfAlong), groundAt(-halfAcross, halfAlong)];
    if ((!scene.roof || supportMesh) && (!roofBoundary || !supportMesh)) poly(groundCorners, groundFill, null);
    drawGround();

    // The distant ridge is background scenery. Paint it before the building,
    // roof and modules so its silhouette cannot make a rooftop look submerged.
    if (horizon && o.showHorizon !== false && (!printedArray || o.showHorizon === true)) {
      const ridge = [];
      const base = [];
      for (let bearing = 0; bearing <= 360; bearing += 3) {
        const el = horizonElevationAt(horizon, bearing);
        ridge.push(skyPoint(sunVector(90 - Math.max(0, el), bearing)));
        base.push(skyPoint(sunVector(90, bearing)));
      }
      poly([...ridge, ...base.reverse()], palette.terrain, null);
      ctx.strokeStyle = palette.terrainEdge;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ridge.forEach((p, i) => {
        const q = project(p);
        if (i === 0) ctx.moveTo(q.x, q.y);
        else ctx.lineTo(q.x, q.y);
      });
      ctx.stroke();
      const hi = ridge.reduce((highest, p) => p.z > highest.z ? p : highest, ridge[0]);
      if (hi.z > skyRadius * 0.02) {
        const q = project(v3(hi.x * 0.99, hi.y * 0.99, hi.z + skyRadius * 0.035));
        ctx.fillStyle = palette.terrainEdge;
        ctx.font = "600 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(o.horizonLabel || "far horizon (PVGIS)", q.x, q.y);
      }
    }

    const horizonBlocked = beamBlockedByHorizon(
      horizon, sun.azimuth, 90 - sun.zenith
    );
    const litGround = sunAboveGround(s, geo.slopeDir, geo.slopeGrad) && !horizonBlocked;

    // The building the modules stand on. Its faces go into the same far-to-
    // near order as the neighbouring solids and the modules below, so a
    // neighbour behind the house no longer paints over its walls when the
    // view turns. Back faces are culled. A roof plane is keyed at its
    // FARTHEST point - the whole plane behind everything standing on it -
    // so a module on it is always painted after it, whatever its centroid
    // says; walls take their centroid.
    const supportFacets = [];
    let roofPlaneKey = -Infinity;
    if ((!scene.roof || supportMesh) && supportMesh) {
      for (let t = 0; t < supportMesh.length; t += 9) {
        const points = [0, 3, 6].map((j) => v3(supportMesh[t + j], supportMesh[t + j + 1], supportMesh[t + j + 2]));
        const normal = norm(cross(sub(points[1], points[0]), sub(points[2], points[0])));
        if (dot(normal, camDir) <= 1e-9) continue;
        const depths = points.map((p) => dot(p, camDir));
        const plane = normal.z > 0.1;
        const depth = plane ? Math.min(...depths) : depths.reduce((a, b) => a + b, 0) / 3;
        if (plane) roofPlaneKey = roofPlaneKey === -Infinity ? depth : Math.min(roofPlaneKey, depth);
        supportFacets.push({ points, normal, mask: supportEdges[t / 9], plane, depth });
      }
      supportFacets.forEach((f) => { if (f.plane) f.depth = roofPlaneKey; });
    }
    const drawSupportFacet = ({ points, normal, mask }) => {
      // The same light and the same colours as the neighbouring solids: every
      // roof plane in the roof colour, every wall in the mesh grey.
      const lit = state.night ? 0.25 : 0.35 + 0.65 * Math.abs(dot(normal, s));
      const [r, g, b] = normal.z > 0.1 ? palette.roof : palette.mesh;
      poly(points, `rgb(${Math.round(r * lit)}, ${Math.round(g * lit)}, ${Math.round(b * lit)})`, null);
      outlineFacet(points, mask, palette.edge);
    };

    // The shadows on the ground (or on the roof the modules stand on), as one
    // step of the same order: right after the roof plane and before anything
    // standing on it. The support plane of a roof without valid dimensions is
    // not drawn, and nothing is cast on it.
    const drawGroundShadows = () => {
    if (scene.roof && !supportMesh) return;
    if (roofBoundary) {
      ctx.save();
      ctx.beginPath();
      groundCorners.forEach((p, i) => {
        const q = project(p);
        if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
      });
      ctx.closePath();
      ctx.clip();
    }

    // Shadows belong on the ground, and the ground is the patch drawn above.
    // At a winter sunrise a row throws a shadow hundreds of metres long: left
    // unclipped it walked off the edge of the frame as a grey wedge lying
    // across the sky, which looked like a rendering fault and claimed ground
    // the picture does not show. Sutherland-Hodgman in the ground's own
    // (across, along) coordinates, which is exact rather than a bounding box.
    const onGround = (p) =>
      Math.abs(dot(p, geo.c)) <= halfAcross + 1e-6 &&
      Math.abs(dot(p, geo.u)) <= halfAlong + 1e-6;
    const clipToGround = (pts) => {
      let ring = pts.map((p) => ({ a: dot(p, geo.c), b: dot(p, geo.u) }));
      const limits = [
        [(q) => q.a >= -halfAcross, (p, q) => (-halfAcross - p.a) / (q.a - p.a)],
        [(q) => q.a <= halfAcross, (p, q) => (halfAcross - p.a) / (q.a - p.a)],
        [(q) => q.b >= -halfAlong, (p, q) => (-halfAlong - p.b) / (q.b - p.b)],
        [(q) => q.b <= halfAlong, (p, q) => (halfAlong - p.b) / (q.b - p.b)],
      ];
      limits.forEach(([inside, cut]) => {
        if (ring.length === 0) return;
        const next = [];
        for (let k = 0; k < ring.length; k += 1) {
          const cur = ring[k];
          const prev = ring[(k - 1 + ring.length) % ring.length];
          const curIn = inside(cur);
          if (curIn !== inside(prev)) {
            const t = cut(prev, cur);
            next.push({
              a: prev.a + (cur.a - prev.a) * t,
              b: prev.b + (cur.b - prev.b) * t,
            });
          }
          if (curIn) next.push(cur);
        }
        ring = next;
      });
      return ring.map((q) => groundAt(q.a, q.b));
    };

    /** Cut a ground polygon at a line across the rows, keeping the near side. */
    const clipAcross = (pts, limit, dir) => {
      const side = (p) => dir * (dot(p, geo.c) - limit);
      const out = [];
      for (let k = 0; k < pts.length; k += 1) {
        const cur = pts[k];
        const prev = pts[(k - 1 + pts.length) % pts.length];
        const sc = side(cur);
        const sp = side(prev);
        if ((sc <= 0) !== (sp <= 0)) {
          const t = sp / (sp - sc);
          out.push(v3(
            prev.x + (cur.x - prev.x) * t,
            prev.y + (cur.y - prev.y) * t,
            prev.z + (cur.z - prev.z) * t
          ));
        }
        if (sc <= 0) out.push(cur);
      }
      return out;
    };
    // On a slope the sun can be up and still be below the GROUND plane, and
    // then the entire site is in the hillside's shadow. Every projection has no
    // solution at that point, so the old code drew nothing at all and the
    // picture was indistinguishable from full sun. Now it says what is true:
    // all of it is in shade.
    // Behind the ridge there is no beam at all - which is exactly what
    // pv_core/horizon.py does to the irradiance - so the site is in shade, not
    // striped with row shadows cast by a sun that is not shining on it.
    if (!state.night && !litGround) {
      poly(
        [
          groundAt(-halfAcross, -halfAlong), groundAt(halfAcross, -halfAlong),
          groundAt(halfAcross, halfAlong), groundAt(-halfAcross, halfAlong),
        ],
        palette.shadow,
        null
      );
    }

    if (!state.night && litGround) {
      // Ground shadows: an exact projection of each row's outline along the sun
      // vector onto the terrain plane. Drawn before the rows so a row always
      // sits on top of its own shadow.
      // Only the rows on screen cast. An earlier version continued the field
      // past the slice with invisible "ghost" rows, on the argument that the
      // shadow crossing this ground was really cast by a row off the edge of
      // the picture. That argument is sound and the picture it produced was
      // not: a large shadow lying on bare ground with nothing above it to
      // explain it reads as a fault, and a viewer cannot check a shadow whose
      // caster is not drawn. A shadow the reader can trace back to the thing
      // casting it is worth more here than one extra band of coverage.
      const bands = [];
      // The row behind is not transparent.
      //
      // Each shadow was projected onto the ground plane and nothing else, so a
      // band ran straight through the next row and carried on behind it - which
      // is what the ground would look like if the rows were made of glass.
      //
      // Ray-casting the real geometry gives an exact rule: a row's shadow can
      // never reach more than ONE PITCH past where it begins. A ray that has
      // travelled that far along the ground has risen to the low edge of the
      // next row and is stopped there, so beyond that line the ground is shaded
      // by THAT row, not by this one. Checked against a 3D cast at elevations
      // from 3 to 30 degrees, on-axis and off: the cut lands within 2 cm of
      // where the cast puts it, and at a 3 degree sun it removes 15 m of shadow
      // that was never there.
      const fieldRowCount = Math.max(geo.rows.length, Number(scene.field?.rows) || 0);
      const sliceIsWholeField = fieldRowCount <= geo.rows.length;
      geo.rows.forEach((row) => {
        const corners = [row.low[0], row.low[1], row.high[1], row.high[0]].map((p) =>
          shadowPoint(p, s, geo.slopeDir, geo.slopeGrad)
        );
        if (!corners.every(Boolean)) return;

        const aNear = dot(corners[0], geo.c);
        const aFar = dot(corners[3], geo.c);
        const dir = Math.sign(aFar - aNear);
        // Only where there IS a row behind to do the intercepting. The last row
        // of a finite field throws its shadow onto open ground.
        const hasNext =
          dir !== 0 &&
          (!sliceIsWholeField ||
            geo.rows.some((r) => dir * (r.across - row.across) > 1e-6));
        let clipped = corners;
        if (!geo.individualModules && hasNext && Math.abs(aFar - aNear) > scene.pitch) {
          clipped = clipAcross(corners, aNear + dir * scene.pitch, dir);
        }
        if (clipped.length < 3) return;

        const whole = clipped.every(onGround);
        const visible = whole ? clipped : clipToGround(clipped);
        if (visible.length >= 3) bands.push({ pts: visible, whole });
      });

      // All ground shadows share one translucent fill. Painting the row bands
      // and obstacle separately darkened their overlap, although a point with
      // two blockers loses the same direct beam as a point with one blocker.
      // Keep the grid visible through the shadow and every subpath wound alike
      // so closed obstacle meshes cannot cancel their own silhouettes.
      ctx.beginPath();
      bands.forEach(({ pts }) => {
        sameWinding(pts).forEach((p, i) => {
          const q = project(p);
          if (i === 0) ctx.moveTo(q.x, q.y);
          else ctx.lineTo(q.x, q.y);
        });
        ctx.closePath();
      });

      // Project every obstacle triangle onto the same ground-shadow path.
      let obstacleShadow = false;
      if (groundShadowMesh) {
        for (let t = 0; t < groundShadowMesh.length; t += 9) {
          const pts = groundShadowPolygon([
            v3(groundShadowMesh[t], groundShadowMesh[t + 1], groundShadowMesh[t + 2]),
            v3(groundShadowMesh[t + 3], groundShadowMesh[t + 4], groundShadowMesh[t + 5]),
            v3(groundShadowMesh[t + 6], groundShadowMesh[t + 7], groundShadowMesh[t + 8]),
          ], s, geo.slopeDir, geo.slopeGrad);
          if (pts.length < 3) continue;
          const on = pts.every(onGround) ? pts : clipToGround(pts);
          if (on.length < 3) continue;
          obstacleShadow = true;
          sameWinding(on).forEach((q, i) => {
            const a = project(q);
            if (i === 0) ctx.moveTo(a.x, a.y);
            else ctx.lineTo(a.x, a.y);
          });
          ctx.closePath();
        }
      }
      if (bands.length > 0 || obstacleShadow) {
        ctx.fillStyle = palette.shadow;
        ctx.fill();
      }
    }

    if (roofBoundary) ctx.restore();
    };

    // Paint rows and mesh facets in one far-to-near order. A nearby building
    // must cover the modules, while a building behind them must sit behind.
    const drawMeshFacet = (t) => {
        const p0 = v3(displayMesh[t], displayMesh[t + 1], displayMesh[t + 2]);
        const p1 = v3(displayMesh[t + 3], displayMesh[t + 4], displayMesh[t + 5]);
        const p2 = v3(displayMesh[t + 6], displayMesh[t + 7], displayMesh[t + 8]);
        const e1 = sub(p1, p0);
        const e2 = sub(p2, p0);
        const n = norm(cross(e1, e2));
        const generated = solidFaces.has(Array.from(displayMesh.subarray(t, t + 9)).join(","));
        if (generated && dot(n, camDir) <= 1e-9) return;
        // |cos| rather than cos: STL winding is unreliable enough that trusting
        // it makes half the faces black on a perfectly good file.
        const lit = state.night ? 0.25 : 0.35 + 0.65 * Math.abs(dot(n, s));
        const [r, g, b] = generated && n.z > 0.1 ? palette.roof : palette.mesh;
        poly(
          [p0, p1, p2],
          `rgb(${Math.round(r * lit)}, ${Math.round(g * lit)}, ${Math.round(b * lit)})`,
          !generated && displayMesh.length / 9 < 600 ? palette.meshEdge : null,
          0.5
        );
        if (generated) outlineFacet([p0, p1, p2], displayEdges?.[t / 9], palette.meshEdge);
    };

    // Rows, far to near, so the near ones overlap correctly.
    const sunSide = dot(s, geo.c);
    // The active roof is keyed at its farthest point so the modules on it are
    // always painted after it. That put every other roof face whose middle is
    // nearer than that point AFTER it too, and at some angles a face lying
    // behind it painted over it - the "cut" faces seen while turning the view.
    // What lies wholly on the far side of the active roof's plane cannot cover
    // it, wherever its middle is, so it is painted just before it, in its own
    // order. Nothing else moves.
    const roofFacet = supportFacets.find((f) => f.plane);
    const behindRoof = roofFacet
      ? (pts) => pts.every((p) => dot(sub(p, roofFacet.points[0]), roofFacet.normal) <= 1e-3)
      : () => false;
    const beforeRoof = (depth) => (depth >= roofPlaneKey ? roofPlaneKey - 1e-3 + (depth - roofPlaneKey) * 1e-9 : depth);
    const ordered = geo.rows.map((row) => ({
      row,
      depth: dot(add(add(row.low[0], row.low[1]),
                     add(row.high[0], row.high[1])), camDir) / 4,
    }));
    contextRows.forEach((row) => {
      const depth = dot(add(add(row.low[0], row.low[1]), add(row.high[0], row.high[1])), camDir) / 4;
      ordered.push({ row, context: true, depth: behindRoof([...row.low, ...row.high]) ? beforeRoof(depth) : depth });
    });
    supportFacets.forEach((facet) => ordered.push({ facet, depth: facet.depth }));
    // No building: the shadows go down first, under every row and solid.
    ordered.push({ shadows: true, depth: supportFacets.length ? roofPlaneKey + 1e-6 : -Infinity });
    if (displayMesh) {
      for (let t = 0; t < displayMesh.length; t += 9) {
        const depth = ((displayMesh[t] + displayMesh[t + 3] + displayMesh[t + 6]) * camDir.x +
                  (displayMesh[t + 1] + displayMesh[t + 4] + displayMesh[t + 7]) * camDir.y +
                  (displayMesh[t + 2] + displayMesh[t + 5] + displayMesh[t + 8]) * camDir.z) / 3;
        const behind = roofFacet && behindRoof([0, 3, 6].map((j) => v3(displayMesh[t + j], displayMesh[t + j + 1], displayMesh[t + j + 2])));
        ordered.push({ t, depth: behind ? beforeRoof(depth) : depth });
      }
    }
    ordered.sort((a, b) => a.depth - b.depth);
    // Opaque silhouettes, separate from all physical/shading meshes. Subtract
    // each nearer silhouette from the canvas clip; overlapping occluders must
    // be intersected sequentially, never combined into an XOR hole.
    const occluders = ordered.filter(item => !item.shadows).map(item => {
      const points = item.row ? [...item.row.low, item.row.high[1], item.row.high[0]]
        : item.facet ? item.facet.points
          : [0, 3, 6].map(j => v3(displayMesh[item.t + j], displayMesh[item.t + j + 1], displayMesh[item.t + j + 2]));
      return { item, points, bounds: screenBounds(points.map(project)) };
    });
    const occlusionByItem = new Map(occluders.map(entry => [entry.item, entry]));
    const clipOcclusion = item => {
      const receiver = occlusionByItem.get(item);
      if (!receiver) return;
      occluders.forEach(blocker => {
        if (blocker === receiver || !boundsOverlap(receiver.bounds, blocker.bounds)) return;
        const front = frontOccluder(receiver.points, blocker.points, camDir);
        if (!front.length) return;
        const projected = front.map(project);
        if (!boundsOverlap(receiver.bounds, screenBounds(projected))) return;
        ctx.beginPath();
        ctx.moveTo(-1, -1); ctx.lineTo(W + 1, -1);
        ctx.lineTo(W + 1, H + 1); ctx.lineTo(-1, H + 1); ctx.closePath();
        ctx.moveTo(projected[0].x, projected[0].y);
        projected.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.clip("evenodd");
      });
    };
    const steps =
      scene.verticalModules *
      verticalSegments({
        portrait: scene.module.portrait,
        halfCut: scene.module.halfCut,
        bypassDiodes: scene.module.bypassDiodes,
      });
    const bypassedSteps = Math.ceil(state.bypassed * steps - 1e-9);

    // Project imported obstacle triangles along the beam onto the collector's
    // actual tilted plane, then cut them at its four edges. The ground shadow
    // alone cannot show which modules lose direct light.
    const meshShadowOnRow = (row) => {
      if (!shadowMesh || state.night || !litGround) return;
      const origin = row.low[0];
      const along = sub(row.low[1], origin);
      const across = sub(row.high[0], origin);
      const normal = cross(along, across);
      const beam = dot(s, normal);
      if (Math.abs(beam) < 1e-9) return;
      const along2 = dot(along, along);
      const across2 = dot(across, across);
      const clip = (polygon, axis, limit, keepAbove) => {
        const output = [];
        polygon.forEach((current, i) => {
          const previous = polygon[(i + polygon.length - 1) % polygon.length];
          const cv = current[axis] - limit;
          const pv = previous[axis] - limit;
          const ci = keepAbove ? cv >= -1e-9 : cv <= 1e-9;
          const pi = keepAbove ? pv >= -1e-9 : pv <= 1e-9;
          if (ci !== pi) {
            const f = (limit - previous[axis]) / (current[axis] - previous[axis]);
            output.push({
              u: previous.u + f * (current.u - previous.u),
              v: previous.v + f * (current.v - previous.v),
              t: previous.t + f * (current.t - previous.t),
            });
          }
          if (ci) output.push(current);
        });
        return output;
      };
      const clipEdge = (polygon, a, b, direction, keepInside) => {
        const side = (p) => direction *
          ((b.u - a.u) * (p.v - a.v) - (b.v - a.v) * (p.u - a.u));
        const output = [];
        polygon.forEach((current, i) => {
          const previous = polygon[(i + polygon.length - 1) % polygon.length];
          const cv = side(current);
          const pv = side(previous);
          const ci = keepInside ? cv >= -1e-9 : cv <= 1e-9;
          const pi = keepInside ? pv >= -1e-9 : pv <= 1e-9;
          if (ci !== pi) {
            const f = pv / (pv - cv);
            output.push({
              u: previous.u + f * (current.u - previous.u),
              v: previous.v + f * (current.v - previous.v),
              t: previous.t + f * (current.t - previous.t),
            });
          }
          if (ci) output.push(current);
        });
        return output;
      };
      const blockers = geo.rows.filter((other) => other !== row).map((other) => {
        const otherOrigin = other.low[0];
        const otherNormal = cross(sub(other.low[1], otherOrigin),
                                  sub(other.high[0], otherOrigin));
        const incidence = dot(s, otherNormal);
        if (Math.abs(incidence) < 1e-9) return null;
        const distance = dot(sub(otherOrigin, origin), normal) / beam;
        if (distance <= 1e-6) return null;
        const corners = [other.low[0], other.low[1], other.high[1], other.high[0]]
          .map((p) => {
            const t = dot(sub(p, origin), normal) / beam;
            const hit = sub(sub(p, mul(s, t)), origin);
            return { u: dot(hit, along) / along2,
                     v: dot(hit, across) / across2, t };
          });
        const area = corners.reduce((sum, p, i) => {
          const q = corners[(i + 1) % corners.length];
          return sum + p.u * q.v - q.u * p.v;
        }, 0);
        const bounds = {
          minU: Math.min(...corners.map((p) => p.u)),
          maxU: Math.max(...corners.map((p) => p.u)),
          minV: Math.min(...corners.map((p) => p.v)),
          maxV: Math.max(...corners.map((p) => p.v)),
        };
        if (bounds.maxU < 0 || bounds.minU > 1 ||
            bounds.maxV < 0 || bounds.minV > 1) return null;
        return { distance, corners, direction: Math.sign(area), bounds };
      }).filter(Boolean);
      const polygons = [];
      for (let i = 0; i < shadowMesh.length; i += 9) {
        const vertices = [0, 3, 6].map((j) => v3(
          shadowMesh[i + j], shadowMesh[i + j + 1], shadowMesh[i + j + 2]
        ));
        let polygon = vertices.map((p) => {
          const t = dot(sub(p, origin), normal) / beam;
          const hit = sub(p, mul(s, t));
          const relative = sub(hit, origin);
          return { u: dot(relative, along) / along2,
                   v: dot(relative, across) / across2, t };
        });
        // A module's own triangles (and coplanar neighbours) cannot shadow it.
        // Float32 mesh coordinates need a small world-space tolerance here.
        if (polygon.every((p) => p.t <= 1e-4)) continue;
        // Mixed sides may cross the collector; reject only when all vertices
        // are outside the same half-plane.
        if (["t", "u", "v"].some((axis) => {
          const limits = axis === "t" ? [0, Infinity] : [0, 1];
          return polygon.every((p) => p[axis] < limits[0]) ||
                 polygon.every((p) => p[axis] > limits[1]);
        })) continue;
        polygon = clip(polygon, "t", 1e-4, true);
        polygon = clip(polygon, "u", 0, true);
        polygon = clip(polygon, "u", 1, false);
        polygon = clip(polygon, "v", 0, true);
        polygon = clip(polygon, "v", 1, false);
        if (polygon.length < 3) continue;
        let visible = [polygon];
        blockers.forEach(({ distance, corners, direction, bounds }) => {
          if (!direction) return;
          const next = [];
          visible.forEach((fragment) => {
            if (fragment.every((p) => p.t <= distance + 1e-9) ||
                Math.max(...fragment.map((p) => p.u)) < bounds.minU ||
                Math.min(...fragment.map((p) => p.u)) > bounds.maxU ||
                Math.max(...fragment.map((p) => p.v)) < bounds.minV ||
                Math.min(...fragment.map((p) => p.v)) > bounds.maxV) {
              next.push(fragment);
              return;
            }
            const nearCaster = clip(fragment, "t", distance, false);
            if (nearCaster.length >= 3) next.push(nearCaster);
            let inside = clip(fragment, "t", distance, true);
            for (let edge = 0; edge < corners.length && inside.length >= 3; edge += 1) {
              const a = corners[edge];
              const b = corners[(edge + 1) % corners.length];
              const outside = clipEdge(inside, a, b, direction, false);
              if (outside.length >= 3) next.push(outside);
              inside = clipEdge(inside, a, b, direction, true);
            }
          });
          visible = next;
        });
        visible.forEach((fragment) => {
          const points = fragment.map(({ u, v }) =>
            add(origin, add(mul(along, u), mul(across, v))));
          if (Math.abs(projectedArea(points)) > 1e-6) polygons.push(sameWinding(points));
        });
      }
      if (!polygons.length) return;
      ctx.beginPath();
      polygons.forEach((points) => {
        points.forEach((p, i) => {
          const q = project(p);
          if (i === 0) ctx.moveTo(q.x, q.y);
          else ctx.lineTo(q.x, q.y);
        });
        ctx.closePath();
      });
      ctx.fillStyle = palette.rowShade;
      ctx.fill();
    };

    ordered.forEach(item => {
      const { row, t, context, facet, shadows: groundShadows } = item;
      ctx.save();
      clipOcclusion(item);
      try {
      if (facet) { drawSupportFacet(facet); return; }
      if (groundShadows) { drawGroundShadows(); return; }
      if (!row) { drawMeshFacet(t); return; }
      // The row at the sun-facing end of the slice has nothing in front of it.
      const isFrontRow =
        sunSide > 0
          ? row.across >= Math.max(...geo.rows.map((r) => r.across)) - 1e-9
          : row.across <= Math.min(...geo.rows.map((r) => r.across)) + 1e-9;
      // No beam, no band. The row formula knows nothing about the ridge, so
      // with the sun behind the hills it went on reporting a row shadow and the
      // picture drew it - a shadow cast by a sun that is not shining on the
      // array, on the one ground the same frame had just painted entirely dark.
      const fs = context || geo.individualModules || state.night || isFrontRow || !litGround ? 0 : state.shaded;

      const at = (f, i) =>
        add(row.low[i], mul(sub(row.high[i], row.low[i]), f));

      // Which way the collector is facing, from the row itself rather than
      // from the layout - so a tracker's rotation is included for free.
      const face = norm(cross(sub(row.low[1], row.low[0]), sub(row.high[0], row.low[0])));
      const outward = face.z < 0 ? mul(face, -1) : face;
      // With the sun BEHIND the plane there is no beam on the front at all, so
      // a shaded band across it is describing a shadow in light that is not
      // falling on it. The engine gets zero beam here from the cosine; the
      // picture used to go on drawing the row half dark.
      const frontLit = !state.night && dot(outward, s) > 0;

      poly(
        [row.low[0], row.low[1], row.high[1], row.high[0]],
        frontLit ? palette.panel : palette.panelDim,
        palette.edge,
        1
      );

      // Draw frames in the actual module slots, before all shadow overlays.
      // Cells remain visible. Only electrical-section boundaries are opt-in.
      const individual = context ? Boolean(row.slot) : geo.individualModules;
      const columns = individual ? 1 : Math.max(1, Math.round(row.modules || geo.modulesAlong || 1));
      const tiers = individual ? 1 : Math.max(1, Math.round((context ? row.tiers : scene.verticalModules) || 1));
      const along = sub(row.low[1], row.low[0]);
      const across = sub(row.high[0], row.low[0]);
      const originPx = project(row.low[0]), alongPx = project(row.low[1]), acrossPx = project(row.high[0]);
      const widthPx = Math.hypot(alongPx.x - originPx.x, alongPx.y - originPx.y) / columns;
      const heightPx = Math.hypot(acrossPx.x - originPx.x, acrossPx.y - originPx.y) / tiers;
      if (Math.min(widthPx, heightPx) >= 5) {
        for (let col = 0; col < columns; col++) for (let tier = 0; tier < tiers; tier++) {
          const point = (x, y) => add(row.low[0], add(mul(along, (col + x) / columns), mul(across, (tier + y) / tiers)));
          const u = Math.min(0.045, 0.025 * columns / Math.max(0.001, Math.hypot(along.x, along.y, along.z)));
          const v = Math.min(0.045, 0.025 * tiers / Math.max(0.001, Math.hypot(across.x, across.y, across.z)));
          const frameColor = frontLit ? "#b9c7d3" : "#718394";
          [[point(0, 0), point(1, 0), point(1, v), point(0, v)],
            [point(0, 1 - v), point(1, 1 - v), point(1, 1), point(0, 1)],
            [point(0, v), point(u, v), point(u, 1 - v), point(0, 1 - v)],
            [point(1 - u, v), point(1, v), point(1, 1 - v), point(1 - u, 1 - v)]
          ].forEach((edge) => poly(edge, frameColor, null));
          if (Math.min(widthPx, heightPx) >= (cellLines ? 20 : 55)) {
            const cell = (x, y) => point(u + (1 - 2 * u) * x, v + (1 - 2 * v) * y);
            if (cellLines) {
              cellLines.grid.forEach(([a, b]) => poly([cell(...a), cell(...b)], null, "rgba(210,229,244,0.35)", 0.5));
              if (showSegments) cellLines.boundaries.forEach(([a, b]) => poly([cell(...a), cell(...b)], null, "rgba(235,244,255,0.85)", 1));
            } else {
              // Standard mode has no explicit cell wiring map: retain its
              // schematic cell texture, not invented detailed wiring.
              const portrait = Math.hypot(across.x, across.y, across.z) / tiers >= Math.hypot(along.x, along.y, along.z) / columns;
              const nx = portrait ? 6 : 12, ny = portrait ? 12 : 6;
              for (let i = 1; i < nx; i++) poly([cell(i / nx, 0), cell(i / nx, 1)], null, "rgba(210,229,244,0.20)", 0.5);
              for (let i = 1; i < ny; i++) poly([cell(0, i / ny), cell(1, i / ny)], null, "rgba(210,229,244,0.20)", 0.5);
            }
          }
          if (frontLit) poly([point(u, 1 - v), point(1 - u, 1 - v), point(1 - u, 0.64), point(u, 0.82)],
            "rgba(201,225,248,0.07)", null);
        }
      }

      if (frontLit) meshShadowOnRow(row);
      // Other orientations share the same materials and geometric shadows;
      // the active orientation's row-loss/bypass formula does not apply to them.

      if (fs > 0 && frontLit) {
        poly(
          [row.low[0], row.low[1], at(fs, 1), at(fs, 0)],
          palette.rowShade,
          null
        );
      }
      // The modules themselves, at their true width along the row, so the row
      // reads as a run of panels rather than as one long plank. Skipped when
      // they crowd closer than a few pixels: a hundred joints across a screen
      // width is a grey hatch, which says less than no lines at all.
      // Per row, not per field: the last row of a field whose modules do not
      // divide is genuinely shorter, and drawing the full count of joints on it
      // would squeeze the same panels into less row.
      const modulesInRow = Math.max(
        1, Math.round(row.modules || geo.modulesAlong || 0)
      );
      if (!individual && modulesInRow > 1) {
        const e0 = project(row.low[0]);
        const e1 = project(row.low[1]);
        const spacingPx = Math.hypot(e1.x - e0.x, e1.y - e0.y) / modulesInRow;
        if (spacingPx >= 2) {
          ctx.strokeStyle = palette.edge;
          ctx.lineWidth = 0.6;
          const lowSpan = sub(row.low[1], row.low[0]);
          const highSpan = sub(row.high[1], row.high[0]);
          for (let k = 1; k < modulesInRow; k += 1) {
            const f = k / modulesInRow;
            const a = project(add(row.low[0], mul(lowSpan, f)));
            const b = project(add(row.high[0], mul(highSpan, f)));
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
          // And across the slope, where modules are stacked two or three high.
          // Without these a two-high row read as one very tall panel, which is
          // also the geometry the bypass model is quantising - so the picture
          // should show it.
          const stacked = tiers;
          for (let k = 1; k < stacked; k += 1) {
            const f = k / stacked;
            const a = project(at(f, 0));
            const b = project(at(f, 1));
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      if (context) return;
      if (showSegments && !cellLines && steps > 1) {
        ctx.setLineDash([3, 3]);
        for (let i = 1; i < steps; i += 1) {
          const f = i / steps;
          poly([at(f, 0), at(f, 1)], null, palette.edge, 0.6);
        }
        ctx.setLineDash([]);
      }
      if (showSegments && !o.moduleTopology && !geo.individualModules && !isFrontRow && bypassedSteps > 0 && !state.night && litGround && frontLit) {
        // What the diodes actually switch off, which is more than the shadow
        // covers. This is the whole reason the loss is not the shaded area.
        poly(
          [row.low[0], row.low[1], at(bypassedSteps / steps, 1), at(bypassedSteps / steps, 0)],
          "transparent",
          palette.bypass,
          2
        );
      }
      } finally { ctx.restore(); }
    });

    // North arrow and a sun bearing, so the picture can be read without the
    // readout: which way the array faces is half of what makes a shadow.
    const label = (text, p, color) => {
      const q = project(p);
      ctx.fillStyle = color;
      ctx.font = `600 ${printedArray ? 12 * Math.max(1, Number(o.typeScale) || 1) : 12}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(text, q.x, q.y);
    };
    // Sun path, drawn at the dome radius so the sun marker sits ON its own arc.



    // The hour, as light.
    //
    // The scene was drawn at one brightness all day, so 06:30 and 13:00 differed
    // only in where the shadows lay - and a plant at dawn does not look like a
    // plant at noon. The wash goes over the ground and the array but UNDER the
    // sun, its arc and the compass: those are the instrument, and an instrument
    // that fades at dusk is a worse instrument. It bottoms out well short of
    // black, because the geometry still has to be readable at first light.
    // Deliberately gentle. The first version dimmed hard enough that a winter
    // morning read as overcast and the panel colours went muddy; the hour is
    // worth showing, and it is not worth the picture. Full brightness by 40
    // degrees of elevation, and a floor that keeps a night scene legible.
    const elevationForLight = 90 - sun.zenith;
    const daylight = state.night || elevationForLight <= 0
      ? 0.70
      : 0.74 + 0.26 * Math.min(1, elevationForLight / 40);
    if (daylight < 0.995) {
      ctx.fillStyle = isDark
        ? `rgba(0, 4, 10, ${((1 - daylight) * 0.45).toFixed(3)})`
        : `rgba(21, 32, 54, ${((1 - daylight) * 0.45).toFixed(3)})`;
      ctx.fillRect(0, 0, W, H);
    }

    sunArcs.forEach((arc) => {
      ctx.strokeStyle = arc.accent ? palette.sun : palette.arcFaint;
      ctx.lineWidth = arc.accent ? 1.8 : 1;
      ctx.setLineDash(arc.accent ? [] : [5, 5]);
      ctx.beginPath();
      let drawing = false;
      arc.points.forEach((dir) => {
        if (!dir) { drawing = false; return; }
        const q = project(skyPoint(dir));
        if (!drawing) { ctx.moveTo(q.x, q.y); drawing = true; }
        else ctx.lineTo(q.x, q.y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
      // Hour ticks on the selected day, so the arc can be read as a clock.
      arc.hours.forEach(({ dir, hour }) => {
        const q = project(skyPoint(dir));
        ctx.beginPath();
        ctx.arc(q.x, q.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = palette.sun;
        ctx.fill();
        if (hour % 3 === 0) {
          ctx.fillStyle = palette.text;
          ctx.font = "500 10px system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(`${hour}h`, q.x, q.y - 6);
        }
      });
    });

    // All four points of the compass, not just north. Which way the array faces
    // is half of what makes a shadow, and a single N leaves the reader working
    // out the other three - or, worse, guessing.
    if (printedArray) {
      // The print compass is a small overlay. Including a world-sized compass
      // in the fit makes a long row look tiny in the array detail.
      const centerX = W - 52;
      const centerY = 52;
      ctx.beginPath(); ctx.arc(centerX, centerY, 40, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? "rgba(20,30,45,0.9)" : "rgba(255,255,255,0.9)"; ctx.fill();
      ctx.strokeStyle = palette.terrainEdge; ctx.lineWidth = 1; ctx.stroke();
      const north = compass(0);
      const nx = dot(north, right), ny = -dot(north, up);
      const length = Math.hypot(nx, ny) || 1;
      const dx = nx / length, dy = ny / length;
      ctx.beginPath(); ctx.moveTo(centerX + dx * 22, centerY + dy * 22);
      ctx.lineTo(centerX - dy * 6, centerY + dx * 6);
      ctx.lineTo(centerX + dy * 6, centerY - dx * 6); ctx.closePath();
      ctx.fillStyle = "#2563eb"; ctx.fill();
      (o.compassLabels || ["N", "E", "S", "W"]).forEach((name, i) => {
        const bearing = i * 90;
        const dir = compass(bearing);
        ctx.fillStyle = palette.text;
        ctx.font = `600 ${12 * Math.max(1, Number(o.typeScale) || 1)}px system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(name, centerX + dot(dir, right) * 30, centerY - dot(dir, up) * 30 + 4);
      });
    } else {
      const compassRadius = compassRadiusFit;
      [["N", 0], ["E", 90], ["S", 180], ["W", 270]].forEach(([name, bearing]) => {
        const dir = compass(bearing);
        const p = v3(dir.x * compassRadius, dir.y * compassRadius, 0);
        label(name, v3(p.x, p.y, groundZ0(geo, p) + 0.4), palette.text);
      });
    }
    if (!state.night && (!printedArray || o.showSun === true) &&
        !beamBlockedByHorizon(horizon, sun.azimuth, 90 - sun.zenith)) {
      const sunMark = project(skyPoint(s));
      // A disc with a halo rather than a dot: it reads as the light source, and
      // at a glance tells you how high the sun is without reading a number.
      const R = 11;
      const glow = ctx.createRadialGradient(
        sunMark.x, sunMark.y, R * 0.35, sunMark.x, sunMark.y, R * 3.4
      );
      glow.addColorStop(0, palette.sunGlow);
      glow.addColorStop(1, "rgba(255,255,255,0)");
      ctx.beginPath();
      ctx.arc(sunMark.x, sunMark.y, R * 3.4, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sunMark.x, sunMark.y, R, 0, Math.PI * 2);
      ctx.fillStyle = palette.sun;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sunMark.x, sunMark.y, R * 0.55, 0, Math.PI * 2);
      ctx.fillStyle = palette.sunCore;
      ctx.fill();
      // A short ray towards the array, so the picture reads as "light from
      // there" rather than "a dot up there".
      const rayEnd = project(skyPoint(s, 0.76));
      ctx.strokeStyle = palette.sun;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(sunMark.x, sunMark.y);
      ctx.lineTo(rayEnd.x, rayEnd.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // A snapped module and the neighbour it snapped to, outlined in green:
    // the drop is seen to have landed, and where.
    if (Array.isArray(o.snapGuide) && o.snapGuide.length) {
      ctx.save();
      o.snapGuide.forEach((q) => poly(q.points, q.target ? "rgba(22,163,74,0.10)" : "rgba(22,163,74,0.24)", "#16a34a", q.target ? 1.6 : 2.6));
      ctx.restore();
    }
    if (o.selectionMesh?.length) {
      const points = [];
      for (let i = 0; i < o.selectionMesh.length; i += 3) {
        points.push(project(v3(o.selectionMesh[i], o.selectionMesh[i + 1], o.selectionMesh[i + 2])));
      }
      const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
      ctx.save();
      ctx.strokeStyle = "#e68a00";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(Math.min(...xs) - 4, Math.min(...ys) - 4,
        Math.max(...xs) - Math.min(...xs) + 8, Math.max(...ys) - Math.min(...ys) + 8);
      ctx.restore();
    }
    drawClearanceGuides(ctx, o.clearanceGuides, { project, W, H, isDark: o.isDark, locale: o.clearanceLocale });
    return view;
}
